import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SeriesHeader from '../components/brand/SeriesHeader';
import SeriesFooter from '../components/brand/SeriesFooter';
import { CareerGuidedInput, GatedCareerDisplay } from '../components/career-hero';
import { Button } from '../components/ui/button';
import { 
  Loader2, 
  Scale, 
  ArrowRight,
  CheckCircle,
  Sparkles,
  Shield,
  Zap,
  Lock,
  Clock,
  TrendingUp,
  MapPin,
  AlertTriangle,
  Compass,
  HeartCrack,
  Brain,
  Target
} from 'lucide-react';

/**
 * HP.4: "Should I Stay or Leave?" Hero Page
 * 
 * Target: Professionals actively questioning their career direction
 * Hook: "Figure out your next career move with actual data"
 * Maps to: PF3 Gap Analysis (post-signup deep feature)
 * CTA: "Describe your current and desired role"
 */

const CareerHeroPage = () => {
  const navigate = useNavigate();
  const [flowState, setFlowState] = useState('hero');
  const [inputData, setInputData] = useState(null);
  const [careerData, setCareerData] = useState(null);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const series = {
    name: 'Career Clarity',
    accentColor: '#06b6d4',
    slug: 'career',
    author: { name: 'Paul Hooper' }
  };

  useEffect(() => {
    const pendingUnlock = sessionStorage.getItem('rv_pending_unlock');
    if (pendingUnlock) {
      const data = JSON.parse(pendingUnlock);
      if (data.flow_type === 'career') {
        navigate(`/career/complete?rv_id=${data.review_id}&hash=${data.input_hash}`);
      }
    }
  }, []);

  const handleStartInput = () => {
    setFlowState('input');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleInputComplete = async (data) => {
    setInputData(data);
    setFlowState('generating');
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/review/generate-guest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role: data.role,
          context: data.context,
          achievements: data.achievements,
          challenges: data.challenges || '',
          growth: data.growth || '',
          goals: data.goals || '',
          flow_type: 'career',
          optional_context: data.optional_paste || ''
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Generation failed');
      }

      const result = await response.json();
      setCareerData(result);
      setFlowState('result');
    } catch (err) {
      setError(err.message);
      setFlowState('input');
    }
  };

  const handleStartFresh = () => {
    setFlowState('input');
    setCareerData(null);
    setInputData(null);
    setError(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancel = () => {
    setFlowState('hero');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const painPoints = [
    { icon: Clock, title: "Sunday dread is becoming permanent", description: "The thought of Monday makes your stomach drop. You've tried to ignore it but it's getting louder." },
    { icon: HeartCrack, title: "You're good at this but you hate it", description: "Everyone says you're doing well. You don't feel well. Competence and fulfilment are different things." },
    { icon: AlertTriangle, title: "You've been 'about to leave' for months", description: "You keep nearly applying. Then you don't. The unknown feels scarier than the known." },
    { icon: TrendingUp, title: "There's no growth path here", description: "You've hit the ceiling. No promotion in sight, no new challenges, and you're starting to stagnate." },
    { icon: Compass, title: "You don't know what you actually want", description: "You know what you don't want. But what would make you happy? That's the harder question." },
    { icon: Brain, title: "You're overthinking it", description: "Pros and cons lists. Midnight spirals. Asking friends who all say different things. You need clarity, not more opinions." },
  ];

  const howItWorks = [
    { step: 1, title: "Describe Where You Are", description: "Your current role, what's working, what's pushing you toward change. Honest input gets honest output.", icon: MapPin },
    { step: 2, title: "AI Analyses Your Situation", description: "We look at alignment between your situation, values, and options to identify what's really driving this decision.", icon: Brain },
    { step: 3, title: "See Your Career Analysis", description: "A clear breakdown of stay vs. leave factors, risks, gaps, and a framework to actually make this decision.", icon: Scale },
  ];

  // ── Generating ──
  if (flowState === 'generating') {
    return (
      <div className="min-h-screen bg-gray-950">
        <SeriesHeader series={series} />
        <main className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center" data-testid="career-generating">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#06b6d420' }}>
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#06b6d4' }} />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Analysing your career situation...</h2>
            <p className="text-gray-400 max-w-md mx-auto">Weighing your factors, mapping alignment, and building a decision framework. About 15 seconds.</p>
          </div>
        </main>
        <SeriesFooter series={series} />
      </div>
    );
  }

  // ── Input ──
  if (flowState === 'input') {
    return (
      <div className="min-h-screen bg-gray-950">
        <SeriesHeader series={series} />
        <main className="max-w-4xl mx-auto px-4 py-12">
          {error && (
            <div data-testid="career-error" className="mb-6 p-4 bg-red-900/20 border border-red-800/30 rounded-lg">
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          )}
          <CareerGuidedInput onComplete={handleInputComplete} onCancel={handleCancel} accentColor="#06b6d4" />
        </main>
        <SeriesFooter series={series} />
      </div>
    );
  }

  // ── Result ──
  if (flowState === 'result') {
    return (
      <div className="min-h-screen bg-gray-950">
        <SeriesHeader series={series} />
        <main className="max-w-4xl mx-auto px-4 py-12">
          <GatedCareerDisplay data={careerData} onStartFresh={handleStartFresh} accentColor="#06b6d4" />
        </main>
        <SeriesFooter series={series} />
      </div>
    );
  }

  // ── Hero (default) ──
  return (
    <div data-testid="career-hero-page" className="min-h-screen bg-gray-950">
      <SeriesHeader series={series} />

      <main>
        {/* Hero Section */}
        <section data-testid="career-hero-section" className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-cyan-950/20 via-gray-950 to-gray-950" />
          <div className="relative max-w-6xl mx-auto px-4 py-20 md:py-28">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium mb-6 border" style={{ borderColor: '#06b6d440', color: '#06b6d4', backgroundColor: '#06b6d410' }}>
                <Scale className="w-3.5 h-3.5" />
                Career Clarity — Free Analysis
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight tracking-tight">
                Should I Stay<br />
                <span style={{ color: '#06b6d4' }}>or Leave?</span>
              </h1>

              <p className="text-lg md:text-xl text-gray-300 mb-4 max-w-2xl leading-relaxed">
                You've been going back and forth for weeks. Maybe months. Let's turn that loop into a clear analysis.
              </p>
              <p className="text-base text-gray-400 mb-10 max-w-2xl">
                Describe your situation and we'll map the factors that actually matter — not another pros and cons list, but a real framework for deciding.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  data-testid="career-start-btn"
                  onClick={handleStartInput}
                  className="text-white font-semibold px-8 py-6 text-base rounded-xl shadow-lg"
                  style={{ backgroundColor: '#06b6d4' }}
                >
                  Analyse My Options
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => document.getElementById('career-how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                  className="border-gray-700 text-gray-300 hover:text-white px-6 py-6 text-base rounded-xl"
                >
                  How does it work?
                </Button>
              </div>

              <p className="text-xs text-gray-500 mt-4">No signup required. Free instant analysis. Takes about 2 minutes.</p>
            </div>
          </div>
        </section>

        {/* Pain Points */}
        <section data-testid="career-pain-points" className="py-20 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Sound familiar?</h2>
              <p className="text-gray-400 max-w-lg mx-auto">The stay-or-leave question isn't about logic. It's about clarity you don't have yet.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {painPoints.map((point, i) => {
                const Icon = point.icon;
                return (
                  <div key={i} className="p-5 bg-gray-900/50 border border-gray-800/50 rounded-xl hover:border-gray-700 transition-colors">
                    <Icon className="w-5 h-5 mb-3" style={{ color: '#06b6d4' }} />
                    <h3 className="text-white font-semibold text-sm mb-1.5">{point.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{point.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section id="career-how-it-works" data-testid="career-how-it-works" className="py-20 px-4 bg-gray-900/30">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">How it works</h2>
              <p className="text-gray-400">Three steps. Two minutes. A decision framework, not just more thinking.</p>
            </div>
            <div className="space-y-6">
              {howItWorks.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.step} className="flex items-start gap-5 p-6 bg-gray-950/50 border border-gray-800/50 rounded-xl">
                    <div className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm" style={{ backgroundColor: '#06b6d420', color: '#06b6d4' }}>
                      {item.step}
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                      <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Demo Output */}
        <section data-testid="career-demo-output" className="py-20 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">What you'll get</h2>
              <p className="text-gray-400">A structured analysis of your career situation — not just feelings, but factors.</p>
            </div>
            <div className="p-6 bg-gray-900/60 border border-gray-800 rounded-xl">
              <div className="flex items-center gap-2 mb-4">
                <Scale className="w-4 h-4" style={{ color: '#06b6d4' }} />
                <span className="text-sm font-semibold text-gray-300 uppercase tracking-wider">Sample Career Analysis</span>
              </div>
              <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: '#06b6d408', borderColor: '#06b6d420', borderWidth: 1, borderStyle: 'solid' }}>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Your situation shows a clear pattern: <strong className="text-white">high competence but low fulfilment</strong>. 
                  The factors pushing you to consider leaving are primarily about <strong className="text-white">growth and meaning</strong>, 
                  not compensation or stability — which suggests staying without change will likely increase frustration over time.
                </p>
              </div>
              <div className="space-y-2 mb-4">
                <div className="p-3 bg-gray-800/40 rounded-lg border border-gray-700/30">
                  <div className="flex items-center gap-2">
                    <Target className="w-4 h-4" style={{ color: '#06b6d4' }} />
                    <span className="text-white text-sm font-medium">Growth Ceiling Is Real</span>
                  </div>
                  <p className="text-gray-400 text-xs mt-1.5 ml-6">Your tenure + role description suggests limited progression. The lack of new challenges aligns with...</p>
                </div>
                <div className="p-3 bg-gray-800/40 rounded-lg border border-gray-700/30">
                  <div className="flex items-center gap-2">
                    <Compass className="w-4 h-4" style={{ color: '#06b6d4' }} />
                    <span className="text-white text-sm font-medium">Values Misalignment Increasing</span>
                  </div>
                  <p className="text-gray-400 text-xs mt-1.5 ml-6">What you value (autonomy, impact) vs. what your current role offers shows a widening gap...</p>
                </div>
              </div>
              <div className="relative p-4 rounded-lg border border-dashed border-gray-700 overflow-hidden">
                <div className="absolute inset-0 backdrop-blur-sm bg-gray-900/70 z-10 flex items-center justify-center">
                  <div className="text-center">
                    <Lock className="w-5 h-5 mx-auto mb-2" style={{ color: '#06b6d4' }} />
                    <p className="text-gray-300 text-xs font-medium">Full gap analysis + decision framework + risk assessment</p>
                  </div>
                </div>
                <div className="opacity-20 select-none">
                  <div className="h-4 bg-gray-700/50 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-gray-700/30 rounded w-full mb-3" />
                  <div className="h-4 bg-gray-700/50 rounded w-1/2 mb-2" />
                  <div className="h-3 bg-gray-700/30 rounded w-5/6" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section data-testid="career-final-cta" className="py-20 px-4 bg-gray-900/30">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">Stop going in circles. Get clarity.</h2>
            <p className="text-gray-400 mb-8 max-w-lg mx-auto">
              Two minutes of honest input. A structured career analysis. 
              Replace the midnight spiral with a decision framework.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10 max-w-2xl mx-auto">
              {[
                { icon: CheckCircle, text: "Free — no card required" },
                { icon: Zap, text: "AI-powered analysis" },
                { icon: Shield, text: "Nothing stored without signup" },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="flex items-center justify-center gap-2 text-sm text-gray-300">
                    <Icon className="w-4 h-4" style={{ color: '#06b6d4' }} />
                    {item.text}
                  </div>
                );
              })}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                data-testid="career-final-cta-btn"
                onClick={handleStartInput}
                className="text-white font-semibold px-8 py-6 text-base rounded-xl shadow-lg"
                style={{ backgroundColor: '#06b6d4' }}
              >
                Analyse My Options
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/insight')}
                className="border-gray-700 text-gray-300 hover:text-white px-6 py-6 text-base rounded-xl"
              >
                Try Capability Insight instead
              </Button>
            </div>
          </div>
        </section>
      </main>

      <SeriesFooter series={series} />
    </div>
  );
};

export default CareerHeroPage;
